import { useContext } from '@nuxtjs/composition-api';

import type { ProductInterface } from '~/composables';
import { validateFraction } from '~/diptyqueTheme/helpers/priceFormatter';
import { ProductTypeEnum } from '~/modules/catalog/product/enums/ProductTypeEnum';
import { getPrice } from '~/modules/catalog/product/getters/productGetters';
import type { Product } from '~/modules/catalog/product/types';
import type { ComplexTextValue } from '~/modules/GraphQL/types';

export const PRICE_TYPE = {
  DYNAMIC: 'DYNAMIC',
  FIXED: 'FIXED'
};

export const ATTRIBUTES = {
  SUBTITLE: 'subtitle',
  CAPACITY: 'capacity',
  VARIANT_NAME: 'variant_name',
  EDITORIAL: 'editorial',
  HOVER_ON_CARD: 'hover_on_card',
  PDP_SHORT_DESCRIPTION: 'pdp_short_description',
  PDP_LONG_DESCRIPTION: 'pdp_long_description',
  PLP_DESCRIPTION: 'plp_description',
  REVAMP_RELATED_PRODUCTS: 'revamp_related_products',
  REPLENISHABLE: 'replenishable',
  PREORDER: 'preorder',
  PREORDER_BUTTON_LABEL: 'preorderButtonLabel',
  PREORDER_STOCK_NOTICE: 'preorderStockNotice',
  SHOW_ESTIMATION_DELIVERY_DATE: 'show_estimation_delivery_date'
} as const;

export const isBundleProduct = (product: Product | ProductInterface): boolean => {
  return product?.__typename === ProductTypeEnum.BUNDLE_PRODUCT;
};

export const hasDynamicPrice = (product: Product | ProductInterface): boolean => {
  return product?.price_type === PRICE_TYPE.DYNAMIC;
};

export const hasFixedPrice = (product: Product | ProductInterface): boolean => {
  return product?.price_type === PRICE_TYPE.FIXED;
};

export const getProductSubtitle = (product: Product): string | null => {
  if (!product || !product[ATTRIBUTES.SUBTITLE]) {
    return null;
  }

  return product[ATTRIBUTES.SUBTITLE];
};

export const getProductHoverImage = (product: Product): string | null => {
  if (!product || !product[ATTRIBUTES.HOVER_ON_CARD]) {
    return null;
  }

  return product[ATTRIBUTES.HOVER_ON_CARD];
};

export const getProductPDPShortDescription = (product: Product): string | null => {
  if (!product || !product[ATTRIBUTES.PDP_SHORT_DESCRIPTION]) {
    return null;
  }

  return product[ATTRIBUTES.PDP_SHORT_DESCRIPTION];
};

export const getProductPDPLongDescription = (product: Product): string | null => {
  if (!product || !product[ATTRIBUTES.PDP_LONG_DESCRIPTION]) {
    return null;
  }

  return product[ATTRIBUTES.PDP_LONG_DESCRIPTION];
};

export const getProductPDPCombinedDescription = (product: Product): string | null => {
  if (!product) {
    return null;
  }

  const shortDesc = getProductPDPShortDescription(product);
  const longDesc = getProductPDPLongDescription(product);

  if (!shortDesc && !longDesc) {
    return null;
  }

  return [shortDesc, longDesc].filter(Boolean).join('\n');
};

export const getProductPLPDescription = (product: Product): string | null => {
  if (!product || !product[ATTRIBUTES.PLP_DESCRIPTION]) {
    return '';
  }

  return product[ATTRIBUTES.PLP_DESCRIPTION];
};

export const getProductCapacity = (product: Product | ProductInterface): string | null => {
  if (!product || !product[ATTRIBUTES.CAPACITY]) {
    return null;
  }

  const capacity = product[ATTRIBUTES.CAPACITY];
  return capacity[0]?.label ?? null;
};

export const getProductVariantName = (product: Product | ProductInterface): string | null => {
  if (!product || !product[ATTRIBUTES.VARIANT_NAME]) {
    return null;
  }

  const variant_name = product[ATTRIBUTES.VARIANT_NAME];
  return variant_name[0]?.label ?? null;
};

export const getRelatedProductsQnty = (product: Product | ProductInterface): number | null => {
  if (!product || !product[ATTRIBUTES.REVAMP_RELATED_PRODUCTS]) {
    return null;
  }

  const relatedProducts = product[ATTRIBUTES.REVAMP_RELATED_PRODUCTS];
  const qty = relatedProducts.length > 1 ? relatedProducts.length - 1 : 0;

  return qty;
};

export const getProductEditorial = (product: Product): object[] | null => {
  if (!product || !product[ATTRIBUTES.EDITORIAL]) {
    return null;
  }

  return product[ATTRIBUTES.EDITORIAL].filter((item) => item.label !== 'false');
};

export const getProductReplenishable = (product: Product | ProductInterface): boolean => {
  if (!product || !product[ATTRIBUTES.REPLENISHABLE]) {
    return false;
  }

  return Boolean(product[ATTRIBUTES.REPLENISHABLE]);
};

export const shouldShowEstimationDeliveryDate = (product: Product | ProductInterface): boolean => {
  if (!product || !product[ATTRIBUTES.SHOW_ESTIMATION_DELIVERY_DATE]) {
    return false;
  }

  return Boolean(product[ATTRIBUTES.SHOW_ESTIMATION_DELIVERY_DATE]);
};

export const isProductPreoderEnabled = (product: Product | ProductInterface): boolean => {
  if (!product || !product[ATTRIBUTES.PREORDER]) {
    return false;
  }

  return Boolean(product[ATTRIBUTES.PREORDER]);
};

export const getProductPreorderButtonLabel = (product: Product | ProductInterface): string => {
  if (!product || !product[ATTRIBUTES.PREORDER_BUTTON_LABEL]) {
    return '';
  }

  return product[ATTRIBUTES.PREORDER_BUTTON_LABEL];
};

export const getProductPreorderStockNotice = (product: Product | ProductInterface): string => {
  if (!product || !product[ATTRIBUTES.PREORDER_STOCK_NOTICE]) {
    return '';
  }

  return product[ATTRIBUTES.PREORDER_STOCK_NOTICE];
};

export const getProductCardPrice = (product: Product): string => {
  if (!product || Object.keys(product).length === 0) {
    return 'N/A';
  }

  const {
    app: { i18n }
  } = useContext();
  const regularPrice: string = validateFraction(getPrice(product).regular);
  const isDynamic = hasDynamicPrice(product);
  const bundlePrice = isDynamic ? i18n.t('From {price}', { price: validateFraction(product.custom_set_minimal_price) }) : regularPrice;
  const price = isBundleProduct(product) || isDynamic ? bundlePrice : regularPrice;

  return price as string;
};

export const getProductBasePrice = (product: Product): string => {
  if (!product || Object.keys(product).length === 0) {
    return '';
  }

  const {
    app: { i18n }
  } = useContext();
  const basePrice: string = validateFraction(Number(getPrice(product).base ?? 0));
  const isDynamic = hasDynamicPrice(product);
  const bundlePrice = isDynamic ? i18n.t('From {price}', { price: validateFraction(product.custom_set_minimal_price_excluded_tax) }) : basePrice;
  const price = isBundleProduct(product) || isDynamic ? bundlePrice : basePrice;

  return price as string;
};

export const getOnlyProductCardPrice = (product: Product): string => {
  if (!product || Object.keys(product).length === 0) {
    return 'N/A';
  }

  const regularPrice = String(getPrice(product).regular);
  const isDynamic = hasDynamicPrice(product);
  const bundlePrice = isDynamic ? product.custom_set_minimal_price_excluded_tax : regularPrice;
  const price = isBundleProduct(product) || isDynamic ? bundlePrice : regularPrice;

  return price as string;
};

const customProductGetters = {
  isBundleProduct,
  hasDynamicPrice,
  hasFixedPrice,
  getProductSubtitle,
  getProductCapacity,
  getProductVariantName,
  getProductEditorial,
  getProductHoverImage,
  getProductPLPDescription,
  getRelatedProductsQnty,
  getProductCardPrice,
  getOnlyProductCardPrice,
  getProductBasePrice,
  getProductReplenishable,
  shouldShowEstimationDeliveryDate,
  isProductPreoderEnabled,
  getProductPreorderButtonLabel,
  getProductPreorderStockNotice,
  getProductPDPShortDescription,
  getProductPDPLongDescription,
  getProductPDPCombinedDescription
};

export default customProductGetters;
